import { useState, useEffect, useRef, useCallback } from 'react';
import type { PhotoItem } from '@/types/odoo';
import { usePermissionsStore } from '@/stores/permissions';
import { PlanFileList, planDisplayName } from '@/components/ui/PlanFileList';
import { listProjectPlans, uploadProjectPlan, deleteProjectPlan } from '@/api/tasks';

/* ─── Section 3: Bestanden voor installateurs ─── */

export function GrondplannenSection({
  projectId,
  projectName,
  refreshKey,
}: {
  projectId: number;
  projectName: string;
  refreshKey?: number;
}) {
  const canFeature = usePermissionsStore((s) => s.canFeature);
  const canUpload = canFeature('plans_upload');

  const [plans, setPlans] = useState<PhotoItem[]>([]);
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState<string | null>(null);
  const [uploading, setUploading] = useState(false);
  const [uploadError, setUploadError] = useState<string | null>(null);
  const [deleteConfirm, setDeleteConfirm] = useState<string | null>(null);
  const [deleting, setDeleting] = useState(false);
  const [dragActive, setDragActive] = useState(false);
  const fileInputRef = useRef<HTMLInputElement>(null);
  const dragCounter = useRef(0);

  const loadPlans = useCallback(async () => {
    setLoading(true);
    setLoadError(null);
    try {
      const data = await listProjectPlans(projectId);
      setPlans(data.plans);
    } catch (err) {
      setLoadError(err instanceof Error ? err.message : 'Bestanden ophalen mislukt');
    } finally {
      setLoading(false);
    }
  }, [projectId]);

  useEffect(() => {
    loadPlans();
  }, [loadPlans, refreshKey]);

  const handleUpload = async (files: FileList) => {
    setUploading(true);
    setUploadError(null);
    try {
      for (const file of Array.from(files)) {
        await uploadProjectPlan(projectId, file);
      }
      await loadPlans();
    } catch (err) {
      setUploadError(err instanceof Error ? err.message : 'Upload mislukt');
    } finally {
      setUploading(false);
    }
  };

  const handleDelete = async (key: string) => {
    setDeleting(true);
    try {
      await deleteProjectPlan(projectId, key);
      setDeleteConfirm(null);
      await loadPlans();
    } catch (err) {
      console.error('Failed to delete plan:', err);
      alert('Verwijderen mislukt. Probeer opnieuw.');
    } finally {
      setDeleting(false);
    }
  };

  return (
    <section className="bg-white rounded-xl border border-gray-200 p-5 mb-6">
      <h3 className="text-sm font-extrabold text-td-navy mb-4">Bestanden voor installateurs</h3>

      {loading && (
        <div className="flex items-center justify-center py-6">
          <div className="w-6 h-6 border-2 border-td-blue/30 border-t-td-blue rounded-full animate-spin" role="status">
            <span className="sr-only">Laden...</span>
          </div>
        </div>
      )}

      {!loading && loadError && (
        <div className="text-sm text-red-600 bg-red-50 px-3 py-2 rounded-lg">
          {loadError}
        </div>
      )}

      {!loading && !loadError && plans.length === 0 && (
        <p className="text-sm text-td-muted italic">Geen bestanden beschikbaar</p>
      )}

      {!loading && !loadError && plans.length > 0 && (
        <div className="space-y-2 mb-4">
          <PlanFileList
            plans={plans}
            linkLabel="Bekijken"
            viewLabel="Bekijken"
            linkClassName="shrink-0 px-3 py-1.5 text-xs font-bold text-td-blue hover:text-td-blue/80 transition-colors"
            showMeta
            renderExtra={canUpload ? (plan) => (
              deleteConfirm === plan.key ? (
                <div className="flex items-center gap-1.5 shrink-0">
                  <button
                    type="button"
                    onClick={() => handleDelete(plan.key)}
                    disabled={deleting}
                    className="px-2 py-1 text-xs font-bold text-white bg-red-500 rounded hover:bg-red-600 transition disabled:opacity-50"
                  >
                    {deleting ? '...' : 'Ja'}
                  </button>
                  <button
                    type="button"
                    onClick={() => setDeleteConfirm(null)}
                    className="px-2 py-1 text-xs font-bold text-td-muted hover:text-td-navy transition"
                  >
                    Nee
                  </button>
                </div>
              ) : (
                <button
                  type="button"
                  onClick={() => setDeleteConfirm(plan.key)}
                  className="shrink-0 p-1.5 text-gray-400 hover:text-red-500 transition-colors"
                  aria-label={`Verwijder ${planDisplayName(plan.key)}`}
                >
                  <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24" aria-hidden="true">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                  </svg>
                </button>
              )
            ) : undefined}
          />
        </div>
      )}

      {canUpload && !loading && (
        <div
          onDragEnter={(e) => {
            e.preventDefault();
            e.stopPropagation();
            dragCounter.current++;
            setDragActive(true);
          }}
          onDragOver={(e) => {
            e.preventDefault();
            e.stopPropagation();
          }}
          onDragLeave={(e) => {
            e.preventDefault();
            e.stopPropagation();
            dragCounter.current--;
            if (dragCounter.current === 0) setDragActive(false);
          }}
          onDrop={(e) => {
            e.preventDefault();
            e.stopPropagation();
            dragCounter.current = 0;
            setDragActive(false);
            const files = e.dataTransfer.files;
            if (files.length > 0) handleUpload(files);
          }}
          onClick={() => fileInputRef.current?.click()}
          className={`
            border-2 border-dashed rounded-lg p-4 text-center cursor-pointer transition-colors
            ${dragActive
              ? 'border-td-blue bg-td-blue/5'
              : 'border-gray-200 hover:border-td-blue/50 hover:bg-gray-50'
            }
            ${uploading ? 'opacity-50 pointer-events-none' : ''}
          `}
        >
          {uploading ? (
            <div className="flex items-center justify-center gap-2 py-1">
              <span className="w-4 h-4 border-2 border-td-blue/30 border-t-td-blue rounded-full animate-spin" />
              <span className="text-sm font-semibold text-td-blue">Uploaden...</span>
            </div>
          ) : (
            <div className="flex flex-col items-center gap-1 py-1">
              <svg className="w-6 h-6 text-td-muted" fill="none" stroke="currentColor" viewBox="0 0 24 24" aria-hidden="true">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M7 16a4 4 0 01-.88-7.903A5 5 0 1115.9 6L16 6a5 5 0 011 9.9M15 13l-3-3m0 0l-3 3m3-3v12" />
              </svg>
              <span className="text-sm font-semibold text-td-navy">
                {dragActive ? 'Laat los om te uploaden' : 'Sleep bestanden hierheen'}
              </span>
              <span className="text-xs text-td-muted">of klik om te bladeren</span>
            </div>
          )}
          <input
            ref={fileInputRef}
            type="file"
            multiple
            onChange={(e) => {
              if (e.target.files && e.target.files.length > 0) handleUpload(e.target.files);
              e.target.value = '';
            }}
            className="hidden"
            aria-label="Selecteer bestand om te uploaden"
          />
          {uploadError && (
            <p className="text-xs text-red-500 mt-2">{uploadError}</p>
          )}
        </div>
      )}
    </section>
  );
}
